import React, { useCallback, useMemo, useState } from "react"
import VisibilitySensor from "react-visibility-sensor"

import { useSanity } from "../../../hooks/useSanity"
import { useImage } from "../../../hooks/useImage"
import { useTemplate } from "../../../hooks/useTemplate"
import { useRoutes } from "../../../hooks/useRoutes"
import { useAnalytics } from "../../../analytics/provider"

export const withHero = Component => ({ name = "Hero", slides, ...props }) => {
  const { trackHeroBanner } = useAnalytics()
  const { sanityContent } = useSanity()
  const { getResponsiveImage } = useImage()
  const { linkResolver } = useRoutes()
  const { global } = useTemplate()
  const [active, setActive] = useState(false)
  const [animated, setAnimated] = useState(false)

  const handleTrack = useCallback(
    event =>
      trackHeroBanner({
        ...event,
      }),
    [trackHeroBanner]
  )

  const items = useMemo(
    () =>
      slides?.map((item: any, index: number) => ({
        ...item,
        active: index === active,
        image: getResponsiveImage(item.image, { maxWidth: 2000 }),
        link: linkResolver(item.link?.[0]),
        links: item?.links?.map((link: any) => linkResolver(link)),
        linksContent: sanityContent(item.linksContent),
      })),
    [active, getResponsiveImage, linkResolver, sanityContent, slides]
  )

  const handleSlide = useCallback(
    (index: number) => {
      const label = items[index]?.title || global?.additionalHeroBack

      setActive((prevState: number) => (prevState === index ? false : index))
      handleTrack({
        event: "custom.banner.navigate",
        label,
        data: {
          value: label,
        },
      })
    },
    [global, handleTrack, items, setActive]
  )

  const handleVisibility = useCallback(
    (visible: boolean) => {
      if (visible && !animated) setAnimated(true)
    },
    [animated, setAnimated]
  )

  Component.displayName = name
  return (
    <VisibilitySensor onChange={handleVisibility} partialVisibility>
      <Component {...props} active={active} animated={animated} handleSlide={handleSlide} items={items} />
    </VisibilitySensor>
  )
}
